import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { FormSections, InlineNotification, Input, ModalWindow, Select, Spinner, TextArea, useLocalizeMessage, } from 'libs.nucleus';
import { useCharts, useDatasetVersionCheck } from '../../hooks';
export const EditChartDetailsModal = ({ isOpen, onClose, onSave, chart, isReadOnly = false, }) => {
    const translate = useLocalizeMessage();
    const { updateChartDetails, checkChartNameUnique, fetchDatasets, isLoading: isApiLoading } = useCharts();
    // cubeConfigId/Key may live at root level or inside chart.data depending on the API response
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chartData = chart.data;
    const initialDatasetId = chart.cubeConfigId || chartData?.cubeConfigId || '';
    const initialDatasetKey = chart.cubeConfigKey || chartData?.cubeConfigKey || '';
    const [name, setName] = useState(chart.name);
    const [description, setDescription] = useState(chart.metadata?.description || '');
    const [selectedDatasetId, setSelectedDatasetId] = useState(initialDatasetId);
    const [selectedDatasetKey, setSelectedDatasetKey] = useState(initialDatasetKey);
    const [nameError, setNameError] = useState('');
    const [isValidatingName, setIsValidatingName] = useState(false);
    const [datasets, setDatasets] = useState([]);
    const { isNewerDatasetAvailable, datasetName } = useDatasetVersionCheck({
        cubeConfigId: selectedDatasetId,
        cubeConfigKey: selectedDatasetKey,
        enabled: isOpen,
    });
    // Reset state when chart prop changes
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = chart.data;
        setName(chart.name);
        setDescription(chart.metadata?.description || '');
        setSelectedDatasetId(chart.cubeConfigId || data?.cubeConfigId || '');
        setSelectedDatasetKey(chart.cubeConfigKey || data?.cubeConfigKey || '');
        setNameError('');
    }, [chart]);
    // Load datasets on mount
    useEffect(() => {
        const loadDatasets = async () => {
            const result = await fetchDatasets();
            setDatasets(result);
        };
        loadDatasets();
    }, [fetchDatasets]);
    const validateName = useDebouncedCallback(async (nameValue) => {
        if (!nameValue.trim()) {
            setNameError(translate('Name is required'));
            setIsValidatingName(false);
            return;
        }
        // Skip validation if name hasn't changed
        if (nameValue === chart.name) {
            setNameError('');
            setIsValidatingName(false);
            return;
        }
        setIsValidatingName(true);
        try {
            const isUnique = await checkChartNameUnique(nameValue, chart.id);
            if (!isUnique) {
                setNameError(translate('A chart with this name already exists'));
            }
            else {
                setNameError('');
            }
        }
        catch (error) {
            setNameError('');
        }
        finally {
            setIsValidatingName(false);
        }
    }, 500);
    useEffect(() => {
        if (name) {
            validateName(name);
        }
        else {
            setNameError('');
        }
    }, [name, validateName]);
    const handleNameChange = useCallback((e) => {
        setName(e.target.value);
        setNameError('');
    }, []);
    const handleDescriptionChange = useCallback((e) => {
        setDescription(e.target.value);
    }, []);
    const handleDatasetChange = useCallback((option) => {
        const dataset = datasets.find((d) => d.id === option.value);
        if (dataset) {
            setSelectedDatasetId(dataset.id);
            setSelectedDatasetKey(dataset.key);
        }
    }, [datasets]);
    const isValid = useMemo(() => {
        return name.trim() !== '' && !nameError && !isValidatingName && !!selectedDatasetId;
    }, [name, nameError, isValidatingName, selectedDatasetId]);
    const handleSave = useCallback(async () => {
        if (!isValid) {
            return;
        }
        try {
            const updated = await updateChartDetails(chart.id, {
                name: name.trim(),
                description: description.trim() || undefined,
                cubeConfigId: selectedDatasetId,
                cubeConfigKey: selectedDatasetKey,
            });
            onSave(updated);
            onClose();
        }
        catch (error) {
            // Error handled in hook
        }
    }, [
        isValid,
        updateChartDetails,
        chart.id,
        name,
        description,
        selectedDatasetId,
        selectedDatasetKey,
        onSave,
        onClose,
    ]);
    const datasetOptions = useMemo(() => datasets.map((d) => ({ label: d.name, value: d.id })), [datasets]);
    const formSections = [
        {
            title: translate('Details'),
            content: (_jsxs("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: [_jsxs("div", { className: 'relative', children: [_jsx(Input, { id: 'edit-chart-name', label: translate('Name'), value: name, onChange: handleNameChange, required: true, width: 'full', containLabels: true, hasError: !!nameError, errorMessage: nameError, autoComplete: 'off', disabled: isReadOnly }), isValidatingName && (_jsx("div", { className: 'absolute end-0 top-0', children: _jsx(Spinner, { size: 'sm' }) }))] }), _jsx(TextArea, { id: 'edit-chart-description', label: translate('Description'), value: description, onChange: handleDescriptionChange, width: 'full', disabled: isReadOnly })] })),
        },
        {
            title: translate('Data source'),
            content: (_jsx("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: _jsx(Select, { label: translate('Dataset'), required: true, options: datasetOptions, value: datasetOptions.find((o) => o.value === selectedDatasetId), onChange: handleDatasetChange, width: 'full', disabled: isReadOnly }) })),
        },
    ];
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: isReadOnly ? translate('View chart details') : translate('Edit chart details'), isOpen: true, closeWindow: onClose, footerPrimaryActionButton: isReadOnly
            ? {
                label: translate('Close'),
                onClick: onClose,
                disabled: isApiLoading,
            }
            : {
                label: translate('Save'),
                onClick: handleSave,
                disabled: !isValid || isApiLoading,
            }, footerSecondaryActionButtons: isReadOnly
            ? undefined
            : [
                {
                    label: translate('Cancel'),
                    onClick: onClose,
                    disabled: isApiLoading,
                },
            ], width: 'full', children: _jsxs("div", { className: 'size-full flex flex-col gap-4', children: [isNewerDatasetAvailable && (_jsx(InlineNotification, { type: 'info', title: translate('A newer dataset version is available'), subtitle: datasetName
                        ? translate('A newer version of "{name}" is available in the Library. Update the dataset to ensure this chart is built on the latest version.', { name: datasetName })
                        : translate('A newer version of the selected dataset is available in the Library. Update the dataset to ensure this chart is built on the latest version.') })), _jsx(FormSections, { sections: formSections })] }) }));
};
